import { DatePipe } from '@angular/common';
import { ISearchTableConfig } from '../models/search-table-config.model';

/**
 * Centralized registry for all ConfigurableSearchTableComponent configurations.
 * The keys of this dictionary should match the web address (route path) of the screen.
 */
const datePipe = new DatePipe('en-US')

export const SEARCH_TABLE_CONFIGS: Record<string, ISearchTableConfig> = {



    'administrative-unit-types': {
        pageTitle: 'Administrative Unit Types',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Administrative Unit Types' }
        ],
        searchPanelTitle: 'Search Criteria',
        tablePanelTitle: 'Administrative Unit Types',

        searchFields: [
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'admin-unit-types/search.htm', // Provide real endpoints here
            searchMethod: 'POST', // Searches are typically GET requests

            add: 'admin-unit-types/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'admin-unit-types/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'admin-unit-types/delete.htm',
            deleteMethod: 'POST'
        },

        // Transform internal data into your EXACT payload formats
        payloadBuilders: {
            search: (formValue) => {
                return {
                    autId: "", // Default to 22 per your screenshot/request
                    name: formValue.name || ""
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    autId: null,
                    description: row.description || "",
                    name: row.name || "",
                    status: "I"
                }));
            },
            update: (modifiedRecords: any[]) => {
                // If you have a specific update payload, define it! Otherwise falls back.
                return modifiedRecords.map(row => ({
                    autId: row.autId || row.id,
                    description: row.description,
                    name: row.name,
                    status: "U" // Assuming 'U' for update
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.id || row.autId || 0,
                    autId: row.autId || row.id || 0,
                    description: row.description || "",
                    name: row.name || ""
                }));
            }
        }
    },
    'absence-types': {
        pageTitle: 'Absence Types',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Absence Types' }
        ],
        tablePanelTitle: 'Absence Types',

        searchFields: [
            { key: 'absenceId', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'absenceId', label: 'ID', type: 'text', isEditable: false },
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true, maxLength: 10 },
            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'absence-types/search.htm', // Provide real endpoints here
            searchMethod: 'POST', // Searches are typically GET requests

            add: 'absence-types/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'absence-types/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'absence-types/delete.htm',
            deleteMethod: 'POST'
        },

        // Transform internal data into your EXACT payload formats
        payloadBuilders: {
            search: (formValue) => {
                return {
                    absenceId: formValue.absenceId || '', // Default to 22 per your screenshot/request
                    name: formValue.name || ""
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    absenceId: null,
                    description: row.description || "",
                    courtId: null,
                    name: row.name || "",
                    transType: "I"
                }));
            },
            update: (modifiedRecords: any[]) => {
                // If you have a specific update payload, define it! Otherwise falls back.
                return modifiedRecords.map(row => ({
                    absenceId: row.absenceId || row.id,
                    description: row.description,
                    courtId: null,
                    name: row.name,
                    transType: "U" // Assuming 'U' for update
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.absenceId || row.autId || 0,
                    absenceId: row.absenceId || row.id,
                    description: row.description || "",
                    courtId: null,
                    name: row.name || "",
                    transType: "",
                }));
            }
        }
    }, 'news-and-events': {
        pageTitle: 'News and Events',
        breadcrumbs: [
            { label: 'Admin' },
            { label: 'News and Events' }
        ],
        tablePanelTitle: 'News and Events',

        searchFields: [
            { key: 'newsAndEventId', label: 'ID', type: 'text' },
            { key: 'dateAndTime', label: 'Date', type: 'date' },
            {
                key: 'messageType', label: 'Message Type', type: 'select', options: [
                    { label: 'News', value: 'NEWS' },
                    { label: 'Events', value: 'EVENTS' }
                ]
            },
            { key: 'message', label: 'Message', type: 'text' }
        ],

        columns: [
            { key: 'newsAndEventId', label: 'ID', type: 'text', isEditable: false },
            { key: 'dateAndTime', label: 'Date/Time', type: 'datetime', isEditable: true, required: false },
            {
                key: 'messageType', label: 'Message Type', type: 'select', isEditable: true, required: true, options: [
                    { label: 'News', value: 'News' },
                    { label: 'Events', value: 'Events' }
                ]
            },
            { key: 'message', label: 'Message', type: 'text', isEditable: true, required: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'news-events/search.htm', // Provide real endpoints here
            searchMethod: 'POST', // Searches are typically GET requests

            add: 'news-events/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'news-events/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'news-events/delete.htm',
            deleteMethod: 'POST'
        },

        // Transform internal data into your EXACT payload formats
        payloadBuilders: {
            search: (formValue) => {
                return {
                    newsAndEventId: formValue.newsAndEventId || '',
                    dateAndTime: formValue.dateAndTime
                        ? datePipe.transform(formValue.dateAndTime, 'dd-MMM-yy')
                        : '',
                    messageType: formValue.messageType || "",
                    message: formValue.message || ''
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    newsAndEventId: null, // Default to 22 per your screenshot/request
                    dateAndTime: row.dateAndTime ? new Date(row.dateAndTime).toString() : "",
                    messageType: row.messageType || "",
                    message: row.message || '',
                    transType: "I"
                }));
            },
            update: (modifiedRecords: any[]) => {
                // If you have a specific update payload, define it! Otherwise falls back.
                return modifiedRecords.map(row => ({
                    id: row.newsAndEventId,
                    newsAndEventId: row.newsAndEventId, // Default to 22 per your screenshot/request
                    dateAndTime: datePipe.transform(row.dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"),
                    messageType: row.messageType || "",
                    message: row.message || '',
                    transType: "U" // Assuming 'U' for update
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.newsAndEventId,
                    newsAndEventId: row.newsAndEventId, // Default to 22 per your screenshot/request
                    dateAndTime: datePipe.transform(row.dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"),
                    messageType: row.messageType || "",
                    message: row.message || '',
                }));
            }
        }
    },
    'judge-panel': {
        pageTitle: 'Judge Panel',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Judge Panel' }
        ],
        tablePanelTitle: 'Judge Panel',

        searchFields: [
            { key: 'name', label: 'Name', type: 'text' },
            { key: 'justiceCount', label: 'Judge Count', type: 'text' },

        ],

        columns: [
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'description', label: 'Description', type: 'text', isEditable: true },
            { key: 'justiceCount', label: 'Judge Count', type: 'number', isEditable: true, required: true },
            {
                key: 'panelCategoryName', label: 'Panel Category', type: 'select', isEditable: true, required: true, options: [{ label: 'REGULAR', value: 'REGULAR' },
                { label: 'FULL PANEL', value: 'FULL PANEL' },
                { label: 'FULL COURT PANEL', value: 'FULL COURT PANEL' }]
            }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'mtjustice-panel/search.htm', // Provide real endpoints here
            searchMethod: 'POST', // Searches are typically GET requests

            add: 'mtjustice-panel/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'mtjustice-panel/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'mtjustice-panel/delete.htm',
            deleteMethod: 'POST'
        },

        // Transform internal data into your EXACT payload formats
        payloadBuilders: {
            search: (formValue) => {
                return {
                    justicePanelId: "",
                    name: formValue.name,
                    justiceCount: formValue.justiceCount
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({

                    justicePanelId: null,
                    description: row.description,
                    justiceCount: row.justiceCount,
                    panelCategoryName: row.panelCategoryName || '',
                    panelCategoryId: row.panelCategoryName === 'REGULAR' ? 1 : row.panelCategoryName === 'FULL PANEL' ? 2 : 3,
                    name: row.name,
                    transType: "I"
                }));
            },
            update: (modifiedRecords: any[]) => {
                // If you have a specific update payload, define it! Otherwise falls back.
                return modifiedRecords.map(row => ({
                    id: row.justicePanelId,
                    justicePanelId: row.justicePanelId,
                    description: row.description,
                    justiceCount: row.justiceCount,
                    panelCategoryName: row.panelCategoryName,
                    panelCategoryId: row.panelCategoryName === 'REGULAR' ? 1 : row.panelCategoryName === 'FULL PANEL' ? 2 : 3,
                    name: row.name,
                    transType: "U"
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.justicePanelId,
                    justicePanelId: row.justicePanelId,
                    description: row.description,
                    justiceCount: row.justiceCount,
                    panelCategoryName: row.panelCategoryName,
                    panelCategoryId: row.panelCategoryId,
                    name: row.name,
                    transType: ""
                }));
            }
        }
    },
    'staff-types': {
        pageTitle: 'Staff Types',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Staff Types' }
        ],
        tablePanelTitle: 'Staff Types',

        searchFields: [
            { key: 'id', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' },

        ],

        columns: [
            { key: 'staffTypeId', label: 'ID', type: 'text', isEditable: false },
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'description', label: 'Description', type: 'text', isEditable: true },
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'staff-types/search.htm', // Provide real endpoints here
            searchMethod: 'GET', // Searches are typically GET requests

            add: 'staff-types/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'staff-types/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'staff-types/delete.htm',
            deleteMethod: 'POST'
        },

        // Transform internal data into your EXACT payload formats
        payloadBuilders: {
            search: (formValue) => {
                return {
                    id: formValue.id || '', // Default to 22 per your screenshot/request
                    name: formValue.name || "",
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    staffTypeId: null,
                    description: row.description,
                    name: row.name,
                    transType: "I"
                }));
            },
            update: (modifiedRecords: any[]) => {
                // If you have a specific update payload, define it! Otherwise falls back.
                return modifiedRecords.map(row => ({
                    id: row.staffTypeId,
                    staffTypeId: row.staffTypeId,
                    description: row.description,
                    name: row.name,
                    transType: "U"
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.staffTypeId,
                    staffTypeId: row.staffTypeId,
                    description: row.description,
                    name: row.name,
                    transType: ""
                }));
            }
        }
    },
    'session-master': {
        pageTitle: 'Session Master',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Session Master' }
        ],

        tablePanelTitle: 'Session Master',

        searchFields: [
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'mtSession-master/search.htm',
            searchMethod: 'GET',

            add: 'mtSession-master/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'mtSession-master/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'mtSession-master/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    sessionId: formValue.sessionId || '',
                    name: formValue.name || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    sessionId: null,
                    description: row.description || '',
                    name: row.name || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.sessionId || row.id,
                    sessionId: row.sessionId,
                    description: row.description || '',
                    name: row.name || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.sessionId || row.id,
                    sessionId: row.sessionId,
                    description: row.description || '',
                    name: row.name || '',
                    transType: ''
                }));
            }
        }
    },
    'lower-courts': {
        pageTitle: 'Lower Courts',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Lower Courts' }
        ],

        tablePanelTitle: 'Lower Courts',

        searchFields: [
            { key: 'appealCourtsId', label: 'ID', type: 'text' },
            { key: 'appealCourtsName', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'appealCourtsId', label: 'ID', type: 'text', isEditable: false },
            { key: 'appealCourtsName', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'appealCourtsDescription', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'mt-lowercourts/search.htm',
            searchMethod: 'GET',

            add: 'mt-lowercourts/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'mt-lowercourts/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'mt-lowercourts/delete.htm',
            deleteMethod: 'POST',
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    appealCourtsId: formValue.appealCourtsId || '',
                    appealCourtsName: formValue.appealCourtsName || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    appealCourtsId: null,
                    appealCourtsDescription: row.appealCourtsDescription || '',
                    appealCourtsName: row.appealCourtsName || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    appealCourtsId: row.appealCourtsId || row.id,
                    appealCourtsDescription: row.appealCourtsDescription || '',
                    appealCourtsName: row.appealCourtsName || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    appealCourtsId: row.appealCourtsId || row.id,
                    appealCourtsDescription: row.appealCourtsDescription || '',
                    appealCourtsName: row.appealCourtsName || '',
                    transType: ''
                }));
            }

        }
    },
    'id-structure-types': {
        pageTitle: 'ID Structure Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'ID Structure Types' }
        ],

        tablePanelTitle: 'ID Structure Types',

        searchFields: [
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'id-types/fetch.htm',
            searchMethod: 'GET',

            add: 'id-types/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'id-types/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'id-types/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    id: formValue.id || '',
                    name: formValue.name || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    id: null,
                    description: row.description || '',
                    name: row.name || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.id,
                    description: row.description || '',
                    name: row.name || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.id,
                    description: row.description || '',
                    name: row.name || '',
                    transType: ''
                }));
            }
        }
    },
    'event-types': {
        pageTitle: 'Event Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Event Types' }
        ],

        tablePanelTitle: 'Event Types',

        searchFields: [
            { key: 'eventId', label: 'ID', type: 'number' },
            { key: 'eventName', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'eventId', label: 'ID', type: 'number', isEditable: false },
            { key: 'eventName', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'hasDuration', label: 'Has Duration', type: 'checkbox', isEditable: true },
            { key: 'mayBeContainer', label: 'May Be Container', type: 'checkbox', isEditable: true },
            { key: 'eventDescription', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'eventtypes/search.htm',
            searchMethod: 'GET',

            add: 'eventtypes/upsert.htm',
            addMethod: 'POST',

            update: 'eventtypes/upsert.htm',
            updateMethod: 'POST',

            delete: 'eventtypes/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    eventId: formValue.eventId || '',
                    eventName: formValue.eventName || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    eventId: null,
                    eventName: row.eventName || '',
                    eventDescription: row.eventDescription || '',
                    hasDuration: row.hasDuration || false,
                    mayBeContainer: row.mayBeContainer || false,
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    eventId: row.eventId,
                    eventName: row.eventName || '',
                    eventDescription: row.eventDescription || '',
                    hasDuration: row.hasDuration || false,
                    mayBeContainer: row.mayBeContainer || false,
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.eventId,
                    eventId: row.eventId,
                    eventName: row.eventName || '',
                    eventDescription: row.eventDescription || '',
                    hasDuration: row.hasDuration || false,
                    mayBeContainer: row.mayBeContainer || false,
                    transType: null
                }));
            }
        }
    },
    'task-types': {
        pageTitle: 'Task Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Task Types' }
        ],

        tablePanelTitle: 'Task Types',

        searchFields: [
            { key: 'taskId', label: 'ID', type: 'number' },
            { key: 'taskName', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'taskId', label: 'ID', type: 'number', isEditable: false },

            { key: 'taskName', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'taskDescription', label: 'Description', type: 'text', isEditable: true },

            {
                key: 'category',
                label: 'Category',
                type: 'select',
                isEditable: true,
                required: true,
                options: [
                    { label: 'T-Task', value: 'T-Task' },
                    { label: 'N-Notification', value: 'N-Notification' },
                    { label: 'R-Reminder', value: 'R-Reminder' }
                ]
            }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: false
        },

        apiEndpoints: {

            search: 'tasktypes/search.htm',
            searchMethod: 'GET',

            add: 'tasktypes/upsert.htm',
            addMethod: 'POST',

            update: 'tasktypes/upsert.htm',
            updateMethod: 'POST',
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    taskId: formValue.taskId || '',
                    taskName: formValue.taskName || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    taskId: null,
                    taskName: row.taskName || '',
                    taskDescription: row.taskDescription || '',
                    category: row.category || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    taskId: row.taskId,
                    taskName: row.taskName || '',
                    taskDescription: row.taskDescription || '',
                    category: row.category || '',
                    transType: 'U'
                }));
            },
        }
    },
    'administrative-units': {
        pageTitle: 'Administrative Units',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Administrative Units' }
        ],
        tablePanelTitle: 'Administrative Units',

        searchFields: [
            { key: 'name', label: 'Name', type: 'text' },
            { key: 'postalCode', label: 'Postal Code', type: 'text' },

        ],

        columns: [
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'postalCode', label: 'Postal Code', type: 'number', isEditable: true },
            {
                groupName: 'Administrative Unit Type',
                columns: [
                    {
                        key: 'autId', label: 'ID', type: 'select', isEditable: true, required: true,
                        endpoint: 'admin-unit-types/get.htm',
                        endpointMethod: 'GET',
                        endpointValueKey: 'autId',
                        endpointLabelKey: 'autId,name',
                        dependents: [
                            { sourceProperty: 'name', targetKey: 'autName' }
                        ]
                    },
                    { key: 'autName', label: 'Name', type: 'text', isEditable: false }
                ]
            },
            {
                groupName: 'Parent Administrative Unit',
                columns: [
                    {
                        key: 'pauId', label: 'ID', type: 'select', isEditable: true, required: true,
                        endpoint: 'admin-units/get/data.htm',
                        endpointMethod: 'GET',
                        endpointValueKey: 'pauId',
                        endpointLabelKey: 'pauId,pauName',
                        dependents: [
                            { sourceProperty: 'pauName', targetKey: 'pauName' },
                            { sourceProperty: 'patId', targetKey: 'patId' },
                            { sourceProperty: 'patName', targetKey: 'patName' },

                        ]
                    },
                    { key: 'pauName', label: 'Name', type: 'text', isEditable: false }
                ]
            },

            // 4. Yet Another Grouped Column
            {
                groupName: 'Parent Administrative Unit Type',
                columns: [
                    { key: 'patId', label: 'ID', type: 'text', isEditable: false },
                    { key: 'patName', label: 'Name', type: 'text', isEditable: false }
                ]
            }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'admin-units/search.htm', // Provide real endpoints here
            searchMethod: 'POST', // Searches are typically GET requests

            add: 'admin-units/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'admin-units/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'admin-units/delete.htm',
            deleteMethod: 'POST'
        },

        // Transform internal data into your EXACT payload formats
        payloadBuilders: {
            search: (formValue) => {
                return {
                    amuId: '', // Default to 22 per your screenshot/request
                    name: formValue.name || "",
                    postalCode: formValue.postalCode || ''
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    amuId: row.amuId,
                    postalCode: row.postalCode,
                    pauId: row.pauId,
                    pauName: row.pauName,
                    patId: row.patId,
                    patName: row.patName,
                    autId: row.autId,
                    autName: row.autName,
                    name: row.name,
                    status: "I"
                }));
            },
            update: (modifiedRecords: any[]) => {
                // If you have a specific update payload, define it! Otherwise falls back.
                return modifiedRecords.map(row => ({
                    id: row.amuId,
                    amuId: row.amuId,
                    postalCode: row.postalCode,
                    pauId: row.pauId,
                    pauName: row.pauName,
                    patId: row.patId,
                    patName: row.patName,
                    autId: row.autId,
                    autName: row.autName,
                    name: row.name,
                    status: "U"
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.amuId,
                    amuId: row.amuId,
                    postalCode: row.postalCode,
                    pauId: row.pauId,
                    pauName: row.pauName,
                    patId: row.patId,
                    patName: row.patName,
                    autId: row.autId,
                    autName: row.autName,
                    name: row.name,
                }));
            }
        }
    },
    'party-roles': {
        pageTitle: 'Party Roles',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Party Roles' }
        ],
        tablePanelTitle: 'Party Roles',

        searchFields: [
            { key: 'id', label: 'ID', type: 'text' },
            { key: 'name', label: 'Name', type: 'text' },

        ],

        columns: [
            { key: 'id', label: 'ID', type: 'text', isEditable: false },
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },
            {
                key: 'isRepresentation', label: 'Is Representation?', type: 'select', isEditable: true, required: true, options: [{ label: 'Y', value: 'Y' },
                { label: 'N', value: 'N' }]
            },
            { key: 'description', label: 'Description', type: 'text', isEditable: true },
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'party-roles/search.htm', // Provide real endpoints here
            searchMethod: 'GET', // Searches are typically GET requests

            add: 'party-roles/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'party-roles/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'party-roles/delete.htm',
            deleteMethod: 'POST'
        },

        // Transform internal data into your EXACT payload formats
        payloadBuilders: {
            search: (formValue) => {
                return {
                    id: formValue.id || '', // Default to 22 per your screenshot/request
                    name: formValue.name || ""
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    id: null,
                    description: row.description,
                    transType: "I",
                    name: row.name,
                    isRepresentation: row.isRepresentation === "N" ? false : true
                }));
            },
            update: (modifiedRecords: any[]) => {
                // If you have a specific update payload, define it! Otherwise falls back.
                return modifiedRecords.map(row => ({
                    id: row.id,
                    description: row.description,
                    transType: "I",
                    name: row.name,
                    isRepresentation: row.isRepresentation === "N" ? false : true
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.id,
                    description: row.description,
                    transType: "",
                    name: row.name,
                    isRepresentation: row.isRepresentation === "N" ? false : true
                }));
            }
        }
    },
    'fee-component-types': {
        pageTitle: 'Fee Component Types',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Fee Component Types' }
        ],
        tablePanelTitle: 'Fee Component Types',

        searchFields: [
            { key: 'feeComponentTypesId', label: 'ID', type: 'text' },
            { key: 'feeComponentTypesName', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'feeComponentTypesId', label: 'ID', type: 'text', isEditable: false },
            { key: 'feeComponentTypesName', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'feeComponentTypesDescription', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'feecomponent-types/search.htm',
            searchMethod: 'GET',

            add: 'feecomponent-types/upsert.htm',
            addMethod: 'POST',

            update: 'feecomponent-types/upsert.htm',
            updateMethod: 'POST',

            delete: 'feecomponent-types/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {
            search: (formValue) => {
                return {
                    feeComponentTypesId: formValue.feeComponentTypesId || '',
                    feeComponentTypesName: formValue.feeComponentTypesName || ""
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    feeComponentTypesId: null,
                    feeComponentTypesDescription: row.feeComponentTypesDescription || "",
                    feeComponentTypesName: row.feeComponentTypesName || "",
                    transType: "I"
                }));
            },
            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    feeComponentTypesId: row.feeComponentTypesId || row.id,
                    feeComponentTypesDescription: row.feeComponentTypesDescription,
                    feeComponentTypesName: row.feeComponentTypesName,
                    transType: "U"
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.id || row.autId || 0,
                    feeComponentTypesId: row.feeComponentTypesId || row.id,
                    feeComponentTypesDescription: row.feeComponentTypesDescription || "",
                    feeComponentTypesName: row.feeComponentTypesName || "",
                    transType: "",
                }));
            }
        }
    },

    'payment-types': {
        pageTitle: 'Payment Types',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Payment Types' }
        ],
        tablePanelTitle: 'Payment Types',

        searchFields: [
            { key: 'id', label: 'ID', type: 'text' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'id', label: 'ID', type: 'text', isEditable: false },
            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {
            search: 'payment-types/search.htm',
            searchMethod: 'GET',

            add: 'payment-types/upsert.htm',
            addMethod: 'POST',

            update: 'payment-types/upsert.htm',
            updateMethod: 'POST',

            delete: 'payment-types/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {
            search: (formValue) => {
                return {
                    id: formValue.id || '',
                    name: formValue.name || ""
                };
            },
            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    id: null,
                    description: row.description || "",
                    name: row.name || "",
                    transType: "I"
                }));
            },
            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.id,
                    description: row.description,
                    name: row.name,
                    transType: "U"
                }));
            },
            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.id || row.autId || 0,
                    description: row.description || "",
                    name: row.name || "",
                    transType: "",
                }));
            }
        }
    },

    'party-types': {
        pageTitle: 'Party Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Party Types' }
        ],

        tablePanelTitle: 'Party Types',

        searchFields: [
            { key: 'partyTypeId', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'partyTypeId', label: 'ID', type: 'number', isEditable: false },

            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'party-types/search.htm',
            searchMethod: 'GET',

            add: 'party-types/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'party-types/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'party-types/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    partyTypeId: formValue.partyTypeId || '',
                    name: formValue.name || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    partyTypeId: null,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.partyTypeId,
                    partyTypeId: row.partyTypeId,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.partyTypeId,
                    partyTypeId: row.partyTypeId,
                    name: row.name || '',
                    description: row.description || '',
                    transType: ''
                }));
            }
        }
    },

    'contact-types': {
        pageTitle: 'Contact Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Contact Types' }
        ],

        tablePanelTitle: 'Contact Types',

        searchFields: [
            { key: 'id', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'id', label: 'ID', type: 'number', isEditable: false },

            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'contact-info-types/search.htm',
            searchMethod: 'GET',

            add: 'contact-info-types/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'contact-info-types/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'contact-info-types/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    id: formValue.id || '',
                    name: formValue.name || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    id: null,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.id,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.id,
                    name: row.name || '',
                    description: row.description || '',
                    transType: ''
                }));
            }
        }
    },

    'receipt-types': {
        pageTitle: 'Receipt Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Receipt Types' }
        ],

        tablePanelTitle: 'Receipt Types',

        searchFields: [
            { key: 'id', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'id', label: 'ID', type: 'number', isEditable: false },

            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'receipt-types/search.htm',
            searchMethod: 'GET',

            add: 'receipt-types/upsert.htm',
            addMethod: 'POST',

            update: 'receipt-types/upsert.htm',
            updateMethod: 'POST',

            delete: 'receipt-types/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    id: formValue.id || '',
                    name: formValue.name || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    id: null,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.id,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.id,
                    name: row.name || '',
                    description: row.description || '',
                    transType: ''
                }));
            }
        }
    },

    'document-directions': {
        pageTitle: 'Document Directions',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Document Directions' }
        ],

        tablePanelTitle: 'Document Directions',

        searchFields: [
            { key: 'docDirectionsId', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'docDirectionsId', label: 'ID', type: 'number', isEditable: false },

            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'doc-directions/search.htm',
            searchMethod: 'GET',

            add: 'doc-directions/upsert.htm',
            addMethod: 'POST',

            update: 'doc-directions/upsert.htm',
            updateMethod: 'POST',

            delete: 'doc-directions/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    docDirectionsId: formValue.docDirectionsId || '',
                    name: formValue.name || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    docDirectionsId: null,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.docDirectionsId,
                    docDirectionsId: row.docDirectionsId,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.docDirectionsId,
                    docDirectionsId: row.docDirectionsId,
                    name: row.name || '',
                    description: row.description || '',
                    transType: ''
                }));
            }
        }
    },

    'case-status': {
        pageTitle: 'Case Status',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Case Status' }
        ],

        tablePanelTitle: 'Case Status',

        searchFields: [
            { key: 'caseStatusId', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'caseStatusId', label: 'ID', type: 'number', isEditable: false },

            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'case-status/search.htm',
            searchMethod: 'GET',

            add: 'case-status/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'case-status/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'case-status/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    caseStatusId: formValue.caseStatusId || '',
                    name: formValue.name || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    caseStatusId: null,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.caseStatusId,
                    caseStatusId: row.caseStatusId,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.caseStatusId,
                    caseStatusId: row.caseStatusId,
                    name: row.name || '',
                    description: row.description || '',
                    transType: ''
                }));
            }
        }
    },
    'case-phase-types': {
        pageTitle: 'Case Phase Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Case Phase Types' }
        ],

        tablePanelTitle: 'Case Phase Types',

        searchFields: [
            { key: 'casePhTypesId', label: 'ID', type: 'number' },
            { key: 'casePhTypesName', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'casePhTypesId', label: 'ID', type: 'number', isEditable: false },

            { key: 'casePhTypesName', label: 'Case Ph Types Name', type: 'text', isEditable: true, required: true },

            { key: 'casePhTypesDescription', label: 'Description', type: 'text', isEditable: true },
            { key: 'casePhTypesShortName', label: 'Short Name', type: 'text', isEditable: true, required: true, maxLength: 20 },
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
        },

        apiEndpoints: {

            search: 'casephtypes/search/cphtype.htm',
            searchMethod: 'GET',

            add: 'casephtypes/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'casephtypes/upsert.htm?transType=U',
            updateMethod: 'POST',

        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    casePhTypesId: formValue.casePhTypesId || '',
                    casePhTypesName: formValue.casePhTypesName || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    casePhTypesId: null,
                    casePhTypesName: row.casePhTypesName || '',
                    casePhTypesDescription: row.casePhTypesDescription || '',
                    casePhTypesShortName: row.casePhTypesShortName || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.casePhTypesId,
                    casePhTypesId: row.casePhTypesId,
                    casePhTypesName: row.casePhTypesName || '',
                    casePhTypesDescription: row.casePhTypesDescription || '',
                    casePhTypesShortName: row.casePhTypesShortName || '',
                    transType: 'U'
                }));
            },
        }
    },
    'document-types': {
        pageTitle: 'Document Types',
        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Document Types' }
        ],
        tablePanelTitle: 'Document Types',

        searchFields: [
            { key: 'documentTypeId', label: 'ID', type: 'number' },
            { key: 'documentTypeName', label: 'Name', type: 'text' },

        ],

        columns: [
            { key: 'docTypeId', label: 'ID', type: 'text', isEditable: false },
            { key: 'docTypeName', label: 'Name', type: 'text', isEditable: true, required: true },
            { key: 'docCategory', label: 'Category', type: 'text', isEditable: true, required: true, maxLength: 1 },
            {
                groupName: 'Document Directions',
                columns: [
                    {
                        key: 'docId', label: 'ID', type: 'select', isEditable: true, required: true,
                        endpoint: 'doc-directions/fetch.htm',
                        endpointMethod: 'GET',
                        endpointValueKey: 'docDirectionsId',
                        endpointLabelKey: 'docDirectionsId,name',
                        dependents: [
                            { sourceProperty: 'name', targetKey: 'docDirection' },
                            { sourceProperty: 'description', targetKey: 'docDescription' }
                        ]
                    },
                    { key: 'docDirection', label: 'Name', type: 'text', isEditable: false },
                    { key: 'docDescription', label: 'Description', type: 'text', isEditable: false },
                    {
                        key: 'parentDocId', label: 'Parent Doc ID', type: 'select', isEditable: true, required: false,
                        endpoint: 'document-types/fetch.htm',
                        endpointMethod: 'GET',
                        endpointValueKey: 'docTypeId',
                        endpointLabelKey: 'docTypeId,docTypeName',
                    },
                ]
            },
            { key: 'feeAmount', label: 'Fee Amount', type: 'number', isEditable: true, },
            {
                key: 'activeFlag', label: 'Active Flag', type: 'select', isEditable: true, options: [{ label: 'Y', value: 'Y' },
                { label: 'N', value: 'N' }]
            },
            { key: 'reminder', label: 'Reminder', type: 'number', isEditable: true, },
            {
                key: 'caseCategory', label: 'Case Category', type: 'select', isEditable: true, required: true, options: [{ label: 'ALL', value: 'ALL' },
                { label: 'FIRST INSTANCE', value: 'FI' }, { label: 'APPEAL', value: 'A' }]
            },

        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
        },

        apiEndpoints: {
            search: 'document-types/search.htm', // Provide real endpoints here
            searchMethod: 'POST', // Searches are typically GET requests

            add: 'document-types/upsert.htm',
            addMethod: 'POST',

            update: 'document-types/upsert.htm',
            updateMethod: 'POST',

        },

        // Transform internal data into your EXACT payload formats
        payloadBuilders: {
            search: (formValue) => {
                return {
                    documentTypeId: formValue.documentTypeId || "",
                    documentTypeName: formValue.documentTypeName || ''
                };
            },
            insert: (newRecords: any[]) => {
                const data = newRecords.map(row => ({
                    documentTypeId: 0,
                    documentTypeName: '',
                    docTypeId: null,
                    docId: row.docId || '',
                    docDirection: row.docDirection || '',
                    docDescription: row.docDescription || '',
                    documentTypeCaseTypeId: 0,
                    caseName: '',
                    caseShortName: '',
                    documentTypeCasePhaseTypeId: 0,
                    phaseName: '',
                    phaseShortName: '',
                    transType: "I",
                    feeAmount: row.feeAmount || null,
                    parentDocId: row.parentDocId || null,
                    activeFlag: row.activeFlag || '',
                    reminder: row.reminder || null,
                    caseCategory: row.caseCategory || '',
                    caseCategoryShrtName: '',
                    docTypeName: row.docTypeName || '',
                    docCategory: row.docCategory || ''
                }));
                return { 'insert': data, 'update': [] }
            },
            update: (modifiedRecords: any[]) => {
                // If you have a specific update payload, define it! Otherwise falls back.
                const data = modifiedRecords.map(row => ({
                    documentTypeId: 0,
                    documentTypeName: '',
                    docTypeId: row.docTypeId || '',
                    docId: row.docId || '',
                    docDirection: row.docDirection || '',
                    docDescription: row.docDescription || '',
                    documentTypeCaseTypeId: 0,
                    caseName: '',
                    caseShortName: '',
                    documentTypeCasePhaseTypeId: 0,
                    phaseName: '',
                    phaseShortName: '',
                    transType: "U",
                    feeAmount: row.feeAmount || null,
                    parentDocId: row.parentDocId || null,
                    activeFlag: row.activeFlag || '',
                    reminder: row.reminder || null,
                    caseCategory: row.caseCategory || '',
                    caseCategoryShrtName: '',
                    docTypeName: row.docTypeName || '',
                    docCategory: row.docCategory || ''
                }));
                return { 'insert': [], 'update': data }
            },
        }
    },

    'holidays': {
        pageTitle: 'Holidays',
        breadcrumbs: [
            { label: 'Admin' },
            { label: 'Holidays' }
        ],
        tablePanelTitle: 'Holidays',

        searchFields: [
            { key: 'dateofHoliday', label: 'Date', type: 'date' },
            { key: 'holidayDescription', label: 'Description', type: 'text' }
        ],

        columns: [
            { key: 'holidayId', label: 'ID', type: 'text', isEditable: false },
            { key: 'holidayDate', label: 'Date', type: 'date', isEditable: true, required: true },
            { key: 'holidayDescription', label: 'Description', type: 'text', isEditable: true, required: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'holiday-dates/search.htm',
            searchMethod: 'GET',

            add: 'holiday-dates/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'holiday-dates/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'holiday-dates/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    dateofHoliday: datePipe.transform(formValue.dateofHoliday, 'dd-MMM-yy') || '',
                    holidayDescription: formValue.holidayDescription || ''
                };
            },

            insert: (newRecords: any[]) => {

                const data = newRecords.map(row => ({

                    holidayId: null,
                    holidayDate: new Date(row.holidayDate).toISOString(),
                    holidayDescription: row.holidayDescription,
                    transType: "I"

                }));

                return data;
            },

            update: (modifiedRecords: any[]) => {

                const data = modifiedRecords.map(row => ({

                    id: row.holidayId,
                    holidayId: row.holidayId,
                    holidayDate: row.holidayDate ? new Date(row.holidayDate).toISOString() : null,
                    holidayDescription: row.holidayDescription,
                    transType: "U"

                }));

                return data;
            },

            delete: (selectedRecords: any[]) => {

                const data = selectedRecords.map(row => ({

                    id: row.holidayId,
                    holidayId: row.holidayId,
                    holidayDate: row.holidayDate ? new Date(row.holidayDate).toISOString() : null,
                    holidayDescription: row.holidayDescription,
                    transType: ""

                }));

                return data;
            }

        }
    },
    'court-properties': {
        pageTitle: 'Court Properties',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Court Properties' }
        ],

        tablePanelTitle: 'Court Properties',

        searchFields: [
            {
                key: 'courtId',
                label: 'Court',
                type: 'select',
                required: true,
                endpoint: 'admin-courts/get/courts-lov.htm',
                endpointMethod: 'GET',
                endpointValueKey: 'courtId',
                endpointLabelKey: 'partyName'
            },
            {
                key: 'propertyName',
                label: 'Name',
                type: 'text'
            }
        ],


        columns: [
            { key: 'name', label: 'Name', type: 'text', isEditable: false },
            { key: 'value', label: 'Value', type: 'text', isEditable: true, required: true }
        ],

        actions: {
            showAdd: false,
            showSave: true,
            showModify: true,
            showDelete: false
        },

        apiEndpoints: {

            search: 'admin-court-properties/search.htm',
            searchMethod: 'GET',

            update: 'admin-court-properties/update.htm',
            updateMethod: 'POST',

            // LOV API for Court dropdown
            // courtsLov: 'admin-courts/get/courts-lov.htm',
            // courtsLovMethod: 'GET'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    courtId: formValue.courtId || '',
                    propertyName: formValue.propertyName || ''
                };
            },

            update: (modifiedRecords: any[]) => {

                const data = modifiedRecords.map(row => ({

                    id: row.id,
                    name: row.name,
                    value: row.value,
                    transType: "U"

                }));

                return data;
            }

        }
    },
    'case-types': {
        pageTitle: 'Case Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Case Types' }
        ],

        tablePanelTitle: 'Case Types',

        searchFields: [
            { key: 'id', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'id', label: 'ID', type: 'number', isEditable: false },

            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'description', label: 'Description', type: 'text', isEditable: true },

            { key: 'shortName', label: 'Short Name', type: 'text', isEditable: true, required: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: false,
        },

        apiEndpoints: {

            search: 'case-types/search.htm',
            searchMethod: 'GET',

            add: 'case-types/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'case-types/upsert.htm?transType=U',
            updateMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    id: formValue.id ?? '',
                    name: formValue.name ?? ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    id: null,
                    name: row.name ?? '',
                    description: row.description ?? '',
                    shortName: row.shortName ?? '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.id,
                    name: row.name ?? '',
                    description: row.description ?? '',
                    shortName: row.shortName ?? '',
                    transType: 'U'
                }));
            }
        }
    },
    'appeal-types': {
        pageTitle: 'Appeal Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Appeal Types' }
        ],

        tablePanelTitle: 'Appeal Types',

        searchFields: [
            { key: 'id', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'id', label: 'ID', type: 'number', isEditable: false },

            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'description', label: 'Description', type: 'text', isEditable: true },

            { key: 'shortName', label: 'Short Name', type: 'text', isEditable: true, required: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: false,
        },

        apiEndpoints: {

            search: 'case-types/search.htm',
            searchMethod: 'GET',

            add: 'case-types/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'case-types/upsert.htm?transType=U',
            updateMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    id: formValue.id ?? '',
                    name: formValue.name ?? ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    id: null,
                    name: row.name ?? '',
                    description: row.description ?? '',
                    shortName: row.shortName ?? '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.id,
                    name: row.name ?? '',
                    description: row.description ?? '',
                    shortName: row.shortName ?? '',
                    transType: 'U'
                }));
            }
        }
    },
    'appeal-phase-types': {
        pageTitle: 'Appeal Phase Types',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Appeal Phase Types' }
        ],

        tablePanelTitle: 'Appeal Phase Types',

        searchFields: [
            { key: 'casePhTypesId', label: 'ID', type: 'number' },
            { key: 'casePhTypesName', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'casePhTypesId', label: 'ID', type: 'number', isEditable: false },

            { key: 'casePhTypesName', label: 'Appeal Ph Types Name', type: 'text', isEditable: true, required: true },

            { key: 'casePhTypesDescription', label: 'Description', type: 'text', isEditable: true },
            { key: 'casePhTypesShortName', label: 'Short Name', type: 'text', isEditable: true, required: true },
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
        },

        apiEndpoints: {

            search: 'casephtypes/search/cphtype.htm',
            searchMethod: 'GET',

            add: 'casephtypes/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'casephtypes/upsert.htm?transType=U',
            updateMethod: 'POST',

        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    casePhTypesId: formValue.casePhTypesId || '',
                    casePhTypesName: formValue.casePhTypesName || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    casePhTypesId: null,
                    casePhTypesName: row.casePhTypesName || '',
                    casePhTypesDescription: row.casePhTypesDescription || '',
                    casePhTypesShortName: row.casePhTypesShortName || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.casePhTypesId,
                    casePhTypesId: row.casePhTypesId,
                    casePhTypesName: row.casePhTypesName || '',
                    casePhTypesDescription: row.casePhTypesDescription || '',
                    casePhTypesShortName: row.casePhTypesShortName || '',
                    transType: 'U'
                }));
            },
        }
    },
    'appeal-status': {
        pageTitle: 'Appeal Status',

        breadcrumbs: [
            { label: 'Masters' },
            { label: 'Appeal Status' }
        ],

        tablePanelTitle: 'Appeal Status',

        searchFields: [
            { key: 'caseStatusId', label: 'ID', type: 'number' },
            { key: 'name', label: 'Name', type: 'text' }
        ],

        columns: [
            { key: 'caseStatusId', label: 'ID', type: 'number', isEditable: false },

            { key: 'name', label: 'Name', type: 'text', isEditable: true, required: true },

            { key: 'description', label: 'Description', type: 'text', isEditable: true }
        ],

        actions: {
            showAdd: true,
            showSave: true,
            showModify: true,
            showDelete: true
        },

        apiEndpoints: {

            search: 'case-status/search.htm',
            searchMethod: 'GET',

            add: 'case-status/upsert.htm?transType=I',
            addMethod: 'POST',

            update: 'case-status/upsert.htm?transType=U',
            updateMethod: 'POST',

            delete: 'case-status/delete.htm',
            deleteMethod: 'POST'
        },

        payloadBuilders: {

            search: (formValue) => {
                return {
                    caseStatusId: formValue.caseStatusId || '',
                    name: formValue.name || ''
                };
            },

            insert: (newRecords: any[]) => {
                return newRecords.map(row => ({
                    caseStatusId: null,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'I'
                }));
            },

            update: (modifiedRecords: any[]) => {
                return modifiedRecords.map(row => ({
                    id: row.caseStatusId,
                    caseStatusId: row.caseStatusId,
                    name: row.name || '',
                    description: row.description || '',
                    transType: 'U'
                }));
            },

            delete: (deletedRecords) => {
                return deletedRecords.map(row => ({
                    id: row.caseStatusId,
                    caseStatusId: row.caseStatusId,
                    name: row.name || '',
                    description: row.description || '',
                    transType: ''
                }));
            }
        }
    },

};
SEARCH_TABLE_CONFIGS['justice-panel'] = SEARCH_TABLE_CONFIGS['judge-panel'];


